import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { diffInDays } from '@/helpers/date-helper';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import { shouldClickMask } from '@/components/click-masking/click-masking-config';
import { BuyboxParam, getTranslatableForOfferGraphql } from '@/helpers/offer-helper';

import type { TitleOfferFragment } from './graphql/fragments/Offer.fragment';
import { MonetizationType, PresentationType, ObjectType } from '@/@types/graphql-types';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { observableOptions, type VisibilityChangedPayload } from '@/helpers/composables/useOfferImpressionTracking';

import { getDisneyPromoText, DisneyPlusId } from '@/helpers/disneyPlusPromo';
import { ModalHelper } from '@/helpers/modal-helper';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';

interface BuyBoxOfferProps {
	/**  Bundles get a different iconUrl, this property is added to the offer in BuyBoxRow */
	offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
	offerLinkOverride?: string | null;
	promoted?: boolean;
	objectType: ObjectType;
	hidePresentationTypeLabel?: boolean;
	title?: TitleDetailsNode;
	hideLeavingShowIcon?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxOffer',
  props: {
    offer: null,
    offerLinkOverride: { default: null },
    promoted: { type: Boolean, default: false },
    objectType: null,
    hidePresentationTypeLabel: { type: Boolean, default: false },
    title: null,
    hideLeavingShowIcon: { type: Boolean, default: false }
  },
  emits: ["clickOut", "visibilityChanged"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: BuyBoxOfferProps['offer'];
			isBundle: boolean;
			isLeavingIconShown: boolean;
			evt: MouseEvent;
		}
	): void;
	(
		e: 'visibilityChanged',
		data: VisibilityChangedPayload & {
			offer: BuyBoxOfferProps['offer'] & { isLeavingIconShown: boolean };
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number }, offerLinkOverride: string | null, promoted: boolean, objectType: ObjectType, hidePresentationTypeLabel: boolean, title?: TitleDetailsNode, hideLeavingShowIcon: boolean }

const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');





const { webLocale, country } = useLanguageStore();
const { isLoggedIn } = useUserStore();

const offerId = computed(() => props.offer.bundleId?.toString() ?? props.offer.id);

const shouldMaskClicks = computed(() => shouldClickMask(props.offer.package.packageId, country.value));

const { withArbitrageTracking, trackTagEvent } = useArbitrage({ packageId: 350 /* Apple TV+ */ });

const offerUrl = computed(() => {
	if (isPlexProvider.value) return null;
	if (props.offerLinkOverride) return props.offerLinkOverride;
	if (shouldMaskClicks.value) return props.offer.standardWebURL?.split('?')[0];

	const url = new ClickoutUrl(props.offer)
		.set('uct_country', country.value)
		.set('uct_buybox', props.promoted ? BuyboxParam.Promoted : BuyboxParam.Normal);

	return withArbitrageTracking(url, props.offer).toString();
});

const referrerpolicy = computed(() => (shouldMaskClicks.value ? 'no-referrer' : undefined));

const isLeavingIconShown = computed(() => {
	return (
		!props.promoted &&
		(isLoggedIn.value as boolean) &&
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30
	);
});

const packageImg = computed(() => JW_CONFIG.IMAGESCALER_URL + (props.offer.iconUrl ?? props.offer.package.icon));

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer.package.packageId, webLocale.value)
		? 'nofollow sponsored'
		: 'nofollow'
);

const offerLabel = computed(() => {
	if (isDisneyPromo.value) return disneyPromo.value!.offerText;

	const translation = getTranslatableForOfferGraphql(props.offer, props.objectType);

	if (!translation) return props.offer.retailPrice;

	if (props.offer.monetizationType === MonetizationType.FlatrateAndBuy) {
		return translation + (props.offer?.retailPrice ?? '');
	}

	return translation;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const disneyPromo = computed(() => getDisneyPromoText(country.value));
const isDisneyPromo = computed(
	() => props.offer.package.packageId === DisneyPlusId && !props.offer.bundleId && disneyPromo.value != null
);

function onClickOut(evt: MouseEvent) {
	trackTagEvent(() => props.offer);

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.offer.iconUrl,
		isLeavingIconShown: isLeavingIconShown.value,
		evt,
	});
}

/* OFFER IMPRESSION TRACKING */
const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	emit('visibilityChanged', {
		isVisible,
		id: offerId.value,
		offer: { ...props.offer, isLeavingIconShown: isLeavingIconShown.value },
	});
}

//START AVOD
const isPlexProvider = computed(() => props.offer.package.technicalName === 'plexplayer');

function openTrailerModal() {
	if (!isPlexProvider.value) return;

	ModalHelper.openModal(
		PlexPlayerModal,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
}
//END AVOD

return { __sfc: true,PlexPlayerModal, props, emit, webLocale, country, isLoggedIn, offerId, shouldMaskClicks, withArbitrageTracking, trackTagEvent, offerUrl, referrerpolicy, isLeavingIconShown, packageImg, isSponsoredOffer, offerLabel, presentationTypeLabel, disneyPromo, isDisneyPromo, onClickOut, observeVisibilityOptions, onVisibilityChanged, isPlexProvider, openTrailerModal }
}

})